<script setup lang="ts">
import { SvgOutlineMinus, SvgOutlinePlus, SvgRotateLeft, SvgRotateRight } from '@repo/icons';
import { useStyle3d } from '@repo/style3d';
import { useRelationStore, useSelectionStore } from '@repo/stores';
import { showConfirmDialog } from 'vant';
import { useRouter } from 'vue-router';
import { useParamStore } from '@/store/param.ts';
import { assignNewMaterialIndex } from '@/utils/materialOverride';
import { $t } from '@/locales';

const { setFabricRotation, setFabricCycle, selectMaterial } = useStyle3d();

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const paramStore = useParamStore();
const router = useRouter();

const scale = defineModel<number>('scale', { default: 1 });
const angle = defineModel<number>('angle', { default: 0 });

const onScaleChange = async (scale: number) => {
  const lastSelected = selectionStore.selectedFabricId;

  const lastSelectedShape = relationStore.shapeList.find(item => item.id === lastSelected);
  if (!lastSelectedShape) {
    console.error('shape not found!');
    return;
  }

  const materialIndex = lastSelectedShape.materialIndex ?? 0;

  // 找出所有 main 类型且 materialIndex 相同的版片
  const sameMaterialShapes = relationStore.shapeList.filter(item => {
    if (item.type !== 'main') return false;
    return (item.materialIndex ?? 0) === materialIndex;
  });

  let targetShapes = sameMaterialShapes;

  // 有多个版片共享同一面料，询问是否同步
  if (sameMaterialShapes.length > 1) {
    try {
      await showConfirmDialog({
        title: $t('sync.title'),
        message: $t('sync.message', { count: sameMaterialShapes.length - 1 }),
        confirmButtonText: $t('sync.confirm'),
        cancelButtonText: $t('sync.cancel'),
      });
      // 确认：同步所有
    } catch {
      // 取消：只修改当前版片，并分配新 materialIndex
      const currentFabricId = lastSelectedShape.fabric?.id ?? paramStore.resourceParam.fabricResource.id;
      const newIndex = await assignNewMaterialIndex(
        lastSelectedShape.piece,  // 用 piece 作为稳定唯一标识
        materialIndex,
        relationStore.shapeList,
        router,
        currentFabricId
      );
      if (newIndex !== null) {
        relationStore.updateShape([{ ...lastSelectedShape, materialIndex: newIndex }]);
      }
      targetShapes = [lastSelectedShape];
    }
  }

  for (const shape of targetShapes) {
    await selectMaterial(shape.id);
    await setFabricCycle(scale);
  }
  await selectMaterial(lastSelected);
};

const onAngleChange = async () => {
  await setFabricRotation(angle.value);
};

const onClickMinus = async () => {
  let value = scale.value;
  value = Math.round((value - 0.1) * 10) / 10;
  if (value < 0.1) {
    value = 0.1;
  }
  await onScaleChange(value);
  scale.value = value;
};

const onClickPlus = async () => {
  let value = scale.value;
  value = Math.round((value + 0.1) * 10) / 10;
  if (value > 5.0) {
    value = 5.0;
  }
  await onScaleChange(value);
  scale.value = value;
};

const onClickRotateLeft = async () => {
  angle.value -= 1;
  if (angle.value < 0) {
    angle.value = 360 - angle.value;
  }
  await onAngleChange();
};

const onClickRotateRight = async () => {
  angle.value += 1;
  if (angle.value > 360) {
    angle.value = angle.value - 360;
  }
  await onAngleChange();
};
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.column">
      <div :class="$style.label">{{ $t('fabric-editor.scale') }}</div>
      <div style="display: flex;column-gap: 3px;">
        <SvgOutlineMinus :class="$style.icon" @click="onClickMinus"></SvgOutlineMinus>
        <div :class="$style.icon">{{ scale.toFixed(1) }}</div>
        <SvgOutlinePlus :class="$style.icon" @click="onClickPlus"></SvgOutlinePlus>
      </div>
    </div>
    <div :class="$style.column">
      <div :class="$style.label">{{ $t('fabric-editor.angle') }}</div>
      <div style="display: flex;column-gap: 3px;">
        <SvgRotateLeft :class="$style.icon" @click="onClickRotateLeft"></SvgRotateLeft>
        <div :class="$style.icon">{{ angle }}</div>
        <SvgRotateRight :class="$style.icon" @click="onClickRotateRight"></SvgRotateRight>
      </div>
    </div>
  </div>
</template>
<style module>
.container {
  padding: 20px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 20px;
}

.column {
  display: flex;
  align-items: center;
}

.label {
  margin-right: 10px;
}

.icon {
  border: 1px solid var(--van-gray-4);
  border-radius: 3px;
  width: 27px;
  height: 27px;
  cursor: pointer;
  color: var(--van-button-default-color);
  display: flex;
  align-content: center;
  justify-content: center;
  line-height: 27px;
}
</style>
